import { formatIsoTimestamp, getDisplayDateTimeParts } from '@quyva/common';

const _normalizeKeyPart = (value) => {
    if (value === undefined || value === null) return '';
    return String(value).trim().toLowerCase();
};

const _safeStatusLabelFromApi = (statusLike) => {
    const s = String(statusLike || '').toLowerCase();
    switch (s) {
        case 'success':
        case 'completed':
            return 'Success';
        case 'failed':
        case 'fail':
            return 'Fail';
        // A user-initiated cancel is not a failure - it gets its own label.
        case 'cancelled':
        case 'canceled':
            return 'Cancelled';
        case 'warning':
            return 'Warning';
        // 'waiting' means the run is suspended on user input (a form or an approval) and can
        // be resumed. 'paused' is the older generic value and still maps to Paused so rows
        // recorded before this distinction existed keep rendering.
        case 'waiting':
            return 'Waiting';
        case 'paused':
            return 'Paused';
        case 'processing':
        default:
            return 'Processing';
    }
};

const _safeParts = (isoLike) => {
    const normalized = formatIsoTimestamp(isoLike);
    if (!normalized) return { display: '-', ms: 0 };
    const { date, time } = getDisplayDateTimeParts(normalized);
    const display = date && time ? `${date}, ${time}` : '-';

    let ms = 0;
    try {
        const parseTarget = normalized.includes('T')
            ? normalized
            : normalized.replace(' ', 'T');
        const parsed = Date.parse(parseTarget);
        if (!Number.isNaN(parsed)) ms = parsed;
    } catch (e) {
        ms = 0;
    }

    return { display, ms };
};

export const mapAgentUpdateToRow = ({
    notification,
    currentUserEmail,
}) => {
    if (!notification) return null;

    // Always a string when present: the socket and notification_history (a BigInteger column)
    // can hand this over as a number, and the APIs it is sent to key on a String column.
    const rawProcessId =
        notification.processId || notification.processID || notification.process_id || null;
    const processId = rawProcessId ? String(rawProcessId) : null;
    const userMail = String(
        notification.userMail || notification.user_mail || notification.user_id || ''
    );
    const agentName = String(notification.agentName || notification.agent_name || '');
    const queryHeading = String(
        notification.queryHeading || notification.user_input || notification.query || ''
    );
    const isAsync = Boolean(notification.is_async_agent || notification.isAsyncAgent);
    const executionType = isAsync ? 'ASYNC' : 'SYNC';

    const syntheticMergeKey = [
        _normalizeKeyPart(userMail),
        _normalizeKeyPart(agentName),
        _normalizeKeyPart(queryHeading),
        _normalizeKeyPart(executionType),
    ]
        .filter(Boolean)
        .join('::');

    if ((!processId && !syntheticMergeKey) || !userMail || !agentName) return null;

    const status = _safeStatusLabelFromApi(notification.status);
    const progress = String(notification.status || '').toLowerCase() || 'processing';

    const creationSource =
        notification.start_time ||
        notification.creationTime ||
        notification.creation_time ||
        notification.timeStamp ||
        notification.timestamp;
    const completionSource =
        notification.completion_time || notification.completionTime || notification.timestamp;
    const lastUpdateSource = notification.timeStamp || notification.timestamp || completionSource;

    const { display: creationDisplay, ms: creationMs } = _safeParts(creationSource);
    const { display: completionDisplay, ms: completionMs } = _safeParts(completionSource);
    const { display: lastUpdateDisplayRaw, ms: lastUpdateMsRaw } = _safeParts(lastUpdateSource);

    const isProcessing = String(status).toLowerCase() === 'processing';
    // Both mean "suspended, not finished" - neither should show a completion time.
    const isPaused = ['paused', 'waiting'].includes(String(status).toLowerCase());

    const lastUpdateDisplay =
        lastUpdateDisplayRaw && lastUpdateDisplayRaw !== '-'
            ? lastUpdateDisplayRaw
            : completionDisplay && completionDisplay !== '-'
            ? completionDisplay
            : creationDisplay;
    const lastUpdateMs = lastUpdateMsRaw || completionMs || creationMs;

    const completionTimeDisplay =
        isProcessing || isPaused
            ? ''
            : completionDisplay && completionDisplay !== '-'
            ? completionDisplay
            : '';

    const accessLevel = String(notification.access_level || '').toLowerCase();
    const userType = accessLevel.includes('admin') ? 'Organization Admin' : 'Member';

    const logs = Array.isArray(notification.logs) ? notification.logs : [];
    const message = logs.length
        ? logs
              .map((l) => {
                  const tool = l?.tool_type ? String(l.tool_type) : 'tool';
                  const inst = l?.instance_name ? String(l.instance_name) : '-';
                  const time = (() => {
                      if (l?.execution_time == null) return '';
                      const raw = Number(l.execution_time);
                      if (Number.isNaN(raw)) return '';

                      const isGateway = tool === 'api_gateway' || tool === 'email_gateway';
                      if (isGateway) {
                          const minutes = Math.max(1, Math.round(raw / 60));
                          return `${minutes}m`;
                      }

                      return `${raw.toFixed(2)}s`;
                  })();
                  return `${tool}: ${inst}${time ? ` (${time})` : ''}`;
              })
              .join('\n\n')
        : notification.message || '-';

    const mergeKey = String(processId || syntheticMergeKey);
    const displayUserId =
        currentUserEmail && userMail && currentUserEmail === userMail ? 'self' : userMail;

    return {
        id: mergeKey,
        mergeKey,
        syntheticMergeKey,
        agentName,
        repoName: '',
        queryHeading: queryHeading || agentName,
        executionType,
        userId: displayUserId,
        rawUserMail: userMail,
        userType,
        status,
        progress,
        creationTime: creationDisplay,
        completionTime: completionTimeDisplay,
        lastUpdateTime: lastUpdateDisplay,
        timestampMs: creationMs,
        lastUpdateMs,
        repoId: null,
        message,
        suspensionId: notification.suspension_id ?? notification.suspensionId ?? null,
        // Merged in from suspended_scripts when the process has a waiting suspension.
        // Absent on rows that come from the socket feed, which only carries notifications.
        suspendEventType: notification.suspend_event_type ?? notification.suspendEventType ?? null,
        requiredInput: notification.required_input ?? notification.requiredInput ?? null,
        waitingMessage: notification.waiting_message ?? notification.waitingMessage ?? null,
        processId,
        logs,
    };
};
